import React, { useMemo, useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faSearch,
  faFolder,
  faUpload,
  faCheckCircle,
  faExclamationTriangle,
  faShieldAlt,
  faFolderPlus,
  faEllipsisV,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import CreateComplianceFolderModal from "./CreateComplianceFolderModal";
import ComplianceDocumentCard from "./ComplianceDocumentCard";
import "./ComplianceFolderView.css";
import "./ClientManagement.css";
import {
  fetchComplianceDocuments,
  uploadComplianceDocument,
  deleteComplianceDocument,
  fetchComplianceFolder,
  fetchComplianceFolders,
  createComplianceFolder,
  deleteComplianceFolder,
  renameComplianceFolder,
} from "../utils/api";
import ComplianceFolder from "./ComplianceFolder";
import PageHeader from "./layout/PageHeader";
import { ShieldCheck } from "lucide-react";

const ComplianceFolderView = ({
  activeLocation,
  locations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser,
  onLogout,
  onLock,
}) => {
  const navigate = useNavigate();
  const { folderId } = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [documents, setDocuments] = useState([]);
  const [subfolders, setSubfolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const [isCreateSubfolderModalOpen, setIsCreateSubfolderModalOpen] =
    useState(false);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [sidebarLoading, setSidebarLoading] = useState(false);
  const [expandedFolderIds, setExpandedFolderIds] = useState(() => new Set());
  const [folderMenuOpenFor, setFolderMenuOpenFor] = useState(null);
  const [createParentFolderId, setCreateParentFolderId] = useState(null);
  const uploadInputRef = useRef(null);

  const MAX_FILES_PER_UPLOAD = 10;
  const MAX_FILE_BYTES = 30 * 1024 * 1024;
  const MAX_TOTAL_BYTES = 300 * 1024 * 1024;

  useEffect(() => {
    const onDown = (e) => {
      if (e?.target?.closest?.(".cm-folder-menu")) return;
      setFolderMenuOpenFor(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      const locId = activeLocation?.id || activeLocation?._id;
      if (!locId) {
        setAllFolders([]);
        return;
      }
      setSidebarLoading(true);
      try {
        const folders = await fetchComplianceFolders({ locationId: locId });
        setAllFolders(Array.isArray(folders) ? folders : []);
      } catch (e) {
        console.error("[Compliance] Failed to fetch folders for sidebar", e);
        setAllFolders([]);
      } finally {
        setSidebarLoading(false);
      }
    };
    loadAll();
  }, [activeLocation]);

  const loadFolderDetails = async () => {
    if (!folderId) return;
    setLoading(true);
    try {
      const locId = activeLocation?.id || activeLocation?._id;
      const folderDetails = await fetchComplianceFolder(folderId, {
        locationId: locId || undefined,
      });

      if (!folderDetails || !folderDetails.folder) {
        throw new Error("Folder not found");
      }

      setCurrentFolder(folderDetails.folder);
      setBreadcrumbs(folderDetails.breadcrumbs || []);
      setSubfolders(folderDetails.subfolders || []);
      setDocuments(folderDetails.documents || []);
    } catch (error) {
      console.error("[Compliance] Error loading folder data:", error);
      const serverMessage =
        error?.response?.data?.message ||
        error?.payload?.message ||
        (typeof error?.payload === "string" ? error.payload : "") ||
        error?.message;
      setErrorMessage(serverMessage || "Failed to load compliance documents");
      setCurrentFolder(null);
      setBreadcrumbs([]);
      setSubfolders([]);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFolderDetails();
  }, [folderId, activeLocation]);

  const processFileUpload = async (fileList, inputEl = null) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    if (files.length > MAX_FILES_PER_UPLOAD) {
      setErrorMessage(`You can upload up to ${MAX_FILES_PER_UPLOAD} files at a time.`);
      setTimeout(() => setErrorMessage(""), 5000);
      if (inputEl) inputEl.value = "";
      return;
    }

    const totalBytes = files.reduce((sum, f) => sum + (f?.size || 0), 0);
    if (totalBytes > MAX_TOTAL_BYTES) {
      setErrorMessage("Total upload size exceeds 300MB. Please upload fewer or smaller files.");
      setTimeout(() => setErrorMessage(""), 5000);
      if (inputEl) inputEl.value = "";
      return;
    }

    // Validate file type (PDF, Word, Excel, and Images) with HEIC support
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "image/jpeg",
      "image/png",
      "image/heic",
      "image/heif",
    ];
    const allowedExtensions = [
      ".pdf",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".jpg",
      ".jpeg",
      ".png",
      ".heic",
      ".heif",
    ];
    for (const file of files) {
      const fileExtension = file.name
        .toLowerCase()
        .slice(file.name.lastIndexOf("."));
      if (
        !allowedTypes.includes(file.type) ||
        !allowedExtensions.includes(fileExtension)
      ) {
        setErrorMessage(
          `Unsupported file type: ${file.name}. Allowed: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG, HEIC.`
        );
        setTimeout(() => setErrorMessage(""), 5000);
        if (inputEl) inputEl.value = "";
        return;
      }
      if (file.size > MAX_FILE_BYTES) {
        setErrorMessage(`File too large: ${file.name}. Max size is 30MB each.`);
        setTimeout(() => setErrorMessage(""), 5000);
        if (inputEl) inputEl.value = "";
        return;
      }
    }

    setIsUploading(true);
    setErrorMessage("");

    try {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      formData.append("folderId", folderId);
      if (activeLocation && (activeLocation.id || activeLocation._id)) {
        formData.append("locationId", activeLocation.id || activeLocation._id);
      }

      const uploaded = await uploadComplianceDocument(formData);

      // Safety check for response format
      if (uploaded && (uploaded.success || uploaded._id)) {
        const docsToAdd = uploaded.documents
          || (uploaded.document ? [uploaded.document] : null)
          || (uploaded._id ? [uploaded] : []);
        if (docsToAdd.length > 0) {
          setDocuments((prev) => [...docsToAdd, ...prev]);
        }
        setSuccessMessage(
          docsToAdd.length > 1
            ? `${docsToAdd.length} documents uploaded successfully`
            : "Document uploaded successfully"
        );
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        throw new Error("Upload failed - invalid response from server");
      }

      // Reset file input
      if (inputEl) inputEl.value = "";
    } catch (error) {
      console.error("[Compliance] Failed to upload document:", error);
      const serverMessage =
        error?.message ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.payload?.message ||
        (typeof error?.payload === "string" ? error.payload : "") ||
        (Array.isArray(error?.payload?.errors) ? error.payload.errors.join(", ") : "") ||
        (error?.status === 413
          ? "Upload limit exceeded. Max 10 files, 30MB each, 300MB total."
          : "") ||
        (typeof error?.payload === "string" ? error.payload : "");
      const timeoutMessage =
        error?.code === "ECONNABORTED"
          ? "Upload timed out. Please try a smaller file or retry in a moment."
          : "";
      setErrorMessage(
        serverMessage ||
          timeoutMessage ||
          (error?.request
            ? "Network error while uploading. Please verify API/CORS and try again."
            : error?.message || "Failed to upload document"),
      );
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileInputChange = async (event) => {
    await processFileUpload(event.target.files, event.target);
  };

  const handleUploadDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragActive(false);
    if (isUploading) return;
    await processFileUpload(event.dataTransfer?.files || null, uploadInputRef.current);
  };

  const handleDeleteDocument = async (documentId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this document? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      await deleteComplianceDocument(documentId);
      setDocuments((prev) =>
        prev.filter((doc) => doc.id !== documentId && doc._id !== documentId)
      );
      setSuccessMessage("Document deleted successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) {
      console.error("[Compliance] Failed to delete document:", error);
      setErrorMessage("Failed to delete document");
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const filteredDocuments = documents.filter(
    (doc) =>
      (doc.fileName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.description || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  const displayError = (err) => {
    const message =
      err?.response?.data?.message ||
      err?.message ||
      "Something went wrong. Please try again.";
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(""), 4000);
  };

  const handleCreateSubfolder = async (folderData) => {
    try {
      const locationId =
        activeLocation?.id || activeLocation?._id || activeLocation || null;
      const payload = {
        ...folderData,
        parentFolderId: createParentFolderId || folderId,
        locationId,
      };
      const newFolder = await createComplianceFolder(payload);
      await loadFolderDetails();
      setIsCreateSubfolderModalOpen(false);
      setCreateParentFolderId(null);
      setSuccessMessage("Subfolder created");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("[Compliance] Failed to create subfolder:", err);
      setErrorMessage("Failed to create subfolder");
      setTimeout(() => setErrorMessage(""), 5000);
    }
  };

  const handleRenameSubfolder = async (folder) => {
    const folderIdToRename = folder.id || folder._id;
    const currentName = folder.name || "";
    const newName = window.prompt("Rename folder", currentName);

    if (!newName) return; // cancelled

    const trimmed = newName.trim();
    if (!trimmed) {
      setErrorMessage("Folder name cannot be empty.");
      setTimeout(() => setErrorMessage(""), 4000);
      return;
    }

    if (trimmed === currentName) return; // nothing changed

    try {
      const updatedFolder = await renameComplianceFolder(folderIdToRename, {
        name: trimmed,
      });
      setSubfolders((prev) =>
        prev.map((sub) =>
          sub._id === folder._id || sub.id === folder.id
            ? { ...sub, name: updatedFolder.name }
            : sub
        )
      );
      setSuccessMessage("Folder renamed");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("[Compliance] Failed to rename folder:", err);
      displayError(err);
    }
  };

  const handleDeleteSubfolder = async (folder) => {
    const folderIdToDelete = folder.id || folder._id;
    if (!window.confirm(`Delete "${folder.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteComplianceFolder(folderIdToDelete);
      setSubfolders((prev) =>
        prev.filter(
          (sub) => sub._id !== folder._id && sub.id !== folder.id
        )
      );
      setSuccessMessage("Folder deleted");
      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      console.error("[Compliance] Failed to delete folder:", err);
      displayError(err);
    }
  };

  const handleDeleteSidebarFolder = async (id) => {
    const target = (allFolders || []).find((f) => (f?.id || f?._id) === id);
    const name = target?.name || "this folder";
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await deleteComplianceFolder(id);
      setAllFolders((prev) =>
        (prev || []).filter((f) => (f?.id || f?._id) !== id),
      );
      if (String(folderId) === String(id)) {
        navigate("/compliance-management");
      }
    } catch (e) {
      console.error("[Compliance] Failed to delete folder", e);
      displayError(e);
    }
  };

  const foldersByParent = useMemo(() => {
    const map = new Map();
    (allFolders || []).forEach((f) => {
      const pid = f?.parentId || null;
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid).push(f);
    });
    for (const [, arr] of map.entries()) {
      arr.sort((a, b) =>
        String(a?.name || "").localeCompare(String(b?.name || "")),
      );
    }
    return map;
  }, [allFolders]);

  const getFolderChildren = (parentId) =>
    foldersByParent.get(parentId || null) || [];

  const rootFolders = useMemo(() => getFolderChildren(null), [foldersByParent]);

  const toggleExpanded = (id) => {
    if (!id) return;
    setExpandedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const needsContext = !activeLocation && localStorage.getItem("token");

  return (
    <div className="compliance-folder-view-container page-shell">
      <PageHeader
        title="Compliance Management"
        subtitle="Securely organize compliance documents, licenses, and certifications."
        icon={<ShieldCheck className="h-6 w-6 text-white" />}
        activeLocation={activeLocation}
        locations={locations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={currentUser}
        onLogout={onLogout}
        onLock={onLock}
        showControls={false}
      />

      {needsContext ? (
        <div className="loading-state">Loading workspace context...</div>
      ) : (
        <>
          <CreateComplianceFolderModal
            isOpen={isCreateSubfolderModalOpen}
            onClose={() => {
              setIsCreateSubfolderModalOpen(false);
              setCreateParentFolderId(null);
            }}
            onCreate={handleCreateSubfolder}
          />

          {successMessage && (
            <div className="success-banner">
              <FontAwesomeIcon icon={faCheckCircle} /> {successMessage}
            </div>
          )}

          {errorMessage && (
            <div className="error-banner">
              <FontAwesomeIcon icon={faExclamationTriangle} /> {errorMessage}
            </div>
          )}

          <div
            className={`cm-shell cm-shell--compliance${folderMenuOpenFor ? " cm-shell--menu-open" : ""}`}
          >
            <aside className="cm-sidebar">
              <div className="cm-sidebar-header">
                <div className="cm-sidebar-title">Folders</div>
              </div>

              <div className="cm-folder-list">
                {sidebarLoading ? (
                  <div className="cm-empty">Loading folders...</div>
                ) : rootFolders.length === 0 ? (
                  <div className="cm-empty">No folders yet.</div>
                ) : (
                  (() => {
                    const renderNode = (f, level = 0) => {
                      const id = f?.id || f?._id;
                      const selected = id && String(id) === String(folderId);
                      const displayName = f?.name || "Unnamed Folder";
                      const count = Number(f?.documentCount) || 0;
                      const children = getFolderChildren(id);
                      const hasChildren = children.length > 0;
                      const isExpanded = expandedFolderIds.has(id);

                      return (
                        <div
                          key={id || `${displayName}-${level}`}
                          className="cm-folder-node"
                        >
                          <div
                            role="button"
                            tabIndex={0}
                            className={`cm-folder-item ${
                              selected ? "active" : ""
                            }`}
                            style={{ paddingLeft: 10 + level * 16 }}
                            onClick={() => {
                              if (!id) return;
                              navigate(`/compliance-management/${id}`);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                if (!id) return;
                                navigate(`/compliance-management/${id}`);
                              }
                            }}
                          >
                            <span
                              className="cm-folder-icon"
                              style={
                                f?.color && String(f.color).startsWith("#")
                                  ? {
                                      backgroundColor: `${f.color}22`,
                                      color: f.color,
                                    }
                                  : {}
                              }
                            >
                              <FontAwesomeIcon icon={faFolder} />
                            </span>
                            <span className="cm-folder-text">
                              <span
                                className="cm-folder-name"
                                data-fullname={displayName}
                              >
                                {displayName}
                              </span>
                            </span>
                            <span className="cm-folder-count">{count}</span>
                            <span className="cm-folder-menu">
                              <button
                                type="button"
                                className="cm-folder-kebab"
                                aria-label="Folder actions"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setFolderMenuOpenFor((prev) =>
                                    prev === id ? null : id,
                                  );
                                }}
                              >
                                <FontAwesomeIcon icon={faEllipsisV} />
                              </button>
                              {folderMenuOpenFor === id && (
                                <div className="cm-menu cm-menu-folder">
                                  <button
                                    type="button"
                                    className="cm-menu-item"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setFolderMenuOpenFor(null);
                                      setCreateParentFolderId(id);
                                      setIsCreateSubfolderModalOpen(true);
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faFolderPlus}
                                      style={{ marginRight: 10 }}
                                    />
                                    Create Sub Folder
                                  </button>
                                  <button
                                    type="button"
                                    className="cm-menu-item danger"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setFolderMenuOpenFor(null);
                                      handleDeleteSidebarFolder(id);
                                    }}
                                  >
                                    <FontAwesomeIcon
                                      icon={faTrashAlt}
                                      style={{ marginRight: 10 }}
                                    />
                                    Delete
                                  </button>
                                </div>
                              )}
                            </span>
                            {hasChildren && (
                              <button
                                type="button"
                                className="cm-folder-expand"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleExpanded(id);
                                }}
                              >
                                {isExpanded ? "−" : "+"}
                              </button>
                            )}
                          </div>
                          {hasChildren && isExpanded && (
                            <div className="cm-folder-children">
                              {children.map((c) => renderNode(c, level + 1))}
                            </div>
                          )}
                        </div>
                      );
                    };
                    return rootFolders.map((f) => renderNode(f, 0));
                  })()
                )}
              </div>
            </aside>

            <main className="cm-main">
              <div className="cm-main-card">
                <div className="cm-main-header">
                  <div className="cm-main-title-wrap">
                    <div className="cm-main-title">
                      {currentFolder?.name || "Compliance"}
                    </div>
                    <div className="cm-main-crumb">
                      <Link
                        to="/compliance-management"
                        className="cm-back-link"
                      >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        Back to Compliance Management
                      </Link>
                    </div>
                  </div>
                  <div className="cm-main-actions">
                    <button
                      type="button"
                      className="cm-new-folder"
                      onClick={() => setIsCreateSubfolderModalOpen(true)}
                      style={{ width: "auto", marginTop: 0 }}
                    >
                      <FontAwesomeIcon icon={faFolderPlus} /> New Subfolder
                    </button>
                    <label
                      className="cm-btn cm-btn-analytics"
                      style={{ opacity: isUploading ? 0.6 : 1 }}
                    >
                      <FontAwesomeIcon icon={faUpload} />
                      {isUploading ? " Uploading..." : " Upload Document"}
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.heic,.heif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png,image/heic,image/heif"
                        ref={uploadInputRef}
                        onChange={handleFileInputChange}
                        multiple
                        disabled={isUploading}
                        style={{ display: "none" }}
                      />
                    </label>
                  </div>
                </div>

                <div
                  className={`compliance-upload-dropzone${isDragActive ? " active" : ""}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!isUploading) setIsDragActive(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragActive(false);
                  }}
                  onDrop={handleUploadDrop}
                  onClick={() => !isUploading && uploadInputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if ((e.key === "Enter" || e.key === " ") && !isUploading) {
                      e.preventDefault();
                      uploadInputRef.current?.click();
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faUpload} />
                  <span>
                    Drag & drop files here, or click to browse
                  </span>
                </div>
                <div className="compliance-upload-hint">
                  Upload limits: up to 10 files per upload, max 30MB per file, max 300MB total.
                </div>

                <div className="cm-filters" style={{ marginTop: 12 }}>
                  <div className="cm-search">
                    <FontAwesomeIcon icon={faSearch} className="cm-search-icon" />
                    <input
                      type="text"
                      placeholder="Search documents..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="compliance-folder-body">
                <div className="file-type-notice">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    style={{ color: "#FF9800" }}
                  />
                  <span>
                    Allowed file types: PDF, Word, Excel, and Images (PDF, DOC,
                    DOCX, XLS, XLSX, JPG, PNG, HEIC). Max 10 files per upload,
                    30MB each, 300MB total per upload.
                  </span>
                </div>

                {(loading || subfolders.length > 0) && (
                  <div className="subfolders-section">
                    <div className="section-header">
                      <h3>Subfolders</h3>
                    </div>
                    {loading ? (
                      <div className="loading-state">Loading subfolders...</div>
                    ) : (
                      <div className="subfolders-grid">
                        {subfolders.map((folder) => (
                          <ComplianceFolder
                            key={folder.id || folder._id}
                            folder={folder}
                            onEdit={() => handleRenameSubfolder(folder)}
                            onDelete={() => handleDeleteSubfolder(folder)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="documents-section">
                  <div className="section-header">
                    <h3>Documents</h3>
                  </div>
                  {loading ? (
                    <div className="loading-state">Loading documents...</div>
                  ) : filteredDocuments.length === 0 ? (
                    <div className="empty-state">
                      <FontAwesomeIcon
                        icon={faUpload}
                        style={{ fontSize: "64px", color: "#ccc" }}
                      />
                      <h3>No Documents Uploaded Yet</h3>
                      <p>Upload your first compliance document to get started</p>
                    </div>
                  ) : (
                    <div className="documents-grid">
                      {filteredDocuments.map((doc) => (
                        <ComplianceDocumentCard
                          key={doc.id || doc._id}
                          document={doc}
                          onDelete={handleDeleteDocument}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </main>
          </div>
        </>
      )}
    </div>
  );
};

export default ComplianceFolderView;
